import React from "react";

function safeHref(value) {
    const href = String(value || "").trim();
    if (!href || /^(?:javascript|data|vbscript):/i.test(href)) {
        return null;
    }
    return href;
}

function MarkdownLink({ href, children }) {
    const safeUrl = safeHref(href);
    if (!safeUrl) {
        return <>{children}</>;
    }
    const external = /^[a-z][a-z\d+.-]*:/i.test(safeUrl);
    return (
        <a
            href={safeUrl}
            target={external ? "_blank" : undefined}
            rel={external ? "noreferrer" : undefined}
        >
            {children}
        </a>
    );
}

function InlineMarkdown({ text }) {
    const source = String(text || "");
    const tokenPattern =
        /(\[([^\]]+)\]\(([^)\s]+)\)|`([^`]+)`|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|_([^_]+)_|<((?:https?:\/\/|mailto:)[^>]+)>|(https?:\/\/[^\s<]+))/g;
    const nodes = [];
    let lastIndex = 0;
    let match;

    const appendText = (value) => {
        if (value) {
            nodes.push(
                <React.Fragment key={`text-${nodes.length}`}>{value}</React.Fragment>
            );
        }
    };

    while ((match = tokenPattern.exec(source))) {
        appendText(source.slice(lastIndex, match.index));
        if (match[2] && match[3]) {
            nodes.push(
                <MarkdownLink key={`link-${nodes.length}`} href={match[3]}>
                    {match[2]}
                </MarkdownLink>
            );
        } else if (match[4]) {
            nodes.push(<code key={`code-${nodes.length}`}>{match[4]}</code>);
        } else if (match[5] || match[6]) {
            nodes.push(<strong key={`strong-${nodes.length}`}>{match[5] || match[6]}</strong>);
        } else if (match[7] || match[8]) {
            nodes.push(<em key={`em-${nodes.length}`}>{match[7] || match[8]}</em>);
        } else if (match[9] || match[10]) {
            const href = match[9] || match[10];
            nodes.push(
                <MarkdownLink key={`auto-link-${nodes.length}`} href={href}>
                    {href}
                </MarkdownLink>
            );
        }
        lastIndex = tokenPattern.lastIndex;
    }
    appendText(source.slice(lastIndex));
    return nodes;
}

function isBlockStart(line) {
    return (
        /^\s{0,3}(#{1,6})\s+/.test(line) ||
        /^\s{0,3}(?:[-+*])\s+/.test(line) ||
        /^\s{0,3}\d+[.)]\s+/.test(line) ||
        /^\s{0,3}>\s?/.test(line) ||
        /^\s{0,3}(```|~~~)/.test(line) ||
        /^\s{0,3}(?:\*\s*){3,}$/.test(line) ||
        /^\s{0,3}(?:-\s*){3,}$/.test(line) ||
        /^\s{0,3}(?:_\s*){3,}$/.test(line)
    );
}

function ArchiveMarkdown({ value }) {
    const source = typeof value === "string" ? value.trim() : "";
    if (!source) {
        return null;
    }

    const lines = source.replace(/\r\n?/g, "\n").split("\n");
    const blocks = [];
    let index = 0;

    while (index < lines.length) {
        const line = lines[index];
        if (!line.trim()) {
            index += 1;
            continue;
        }

        const fence = line.match(/^\s{0,3}(```|~~~)\s*([^ ]*)\s*$/);
        if (fence) {
            const fenceMark = fence[1];
            const codeLines = [];
            index += 1;
            while (index < lines.length && !lines[index].match(new RegExp(`^\\s{0,3}${fenceMark}`))) {
                codeLines.push(lines[index]);
                index += 1;
            }
            if (index < lines.length) {
                index += 1;
            }
            blocks.push(
                <pre key={`code-block-${blocks.length}`}>
                    <code>{codeLines.join("\n")}</code>
                </pre>
            );
            continue;
        }

        const heading = line.match(/^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/);
        if (heading) {
            const level = heading[1].length;
            const Heading = `h${level}`;
            blocks.push(
                <Heading key={`heading-${blocks.length}`}>
                    <InlineMarkdown text={heading[2]} />
                </Heading>
            );
            index += 1;
            continue;
        }

        if (/^\s{0,3}(?:\*\s*){3,}$/.test(line) || /^\s{0,3}(?:-\s*){3,}$/.test(line) || /^\s{0,3}(?:_\s*){3,}$/.test(line)) {
            blocks.push(<hr key={`rule-${blocks.length}`} />);
            index += 1;
            continue;
        }

        if (/^\s{0,3}>\s?/.test(line)) {
            const quoteLines = [];
            while (index < lines.length && /^\s{0,3}>\s?/.test(lines[index])) {
                quoteLines.push(lines[index].replace(/^\s{0,3}>\s?/, ""));
                index += 1;
            }
            blocks.push(
                <blockquote key={`quote-${blocks.length}`}>
                    {quoteLines.map((quoteLine, quoteIndex) => (
                        <React.Fragment key={quoteIndex}>
                            <InlineMarkdown text={quoteLine} />
                            {quoteIndex < quoteLines.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                </blockquote>
            );
            continue;
        }

        const unordered = line.match(/^\s{0,3}[-+*]\s+(.+)$/);
        const ordered = line.match(/^\s{0,3}\d+[.)]\s+(.+)$/);
        if (unordered || ordered) {
            const isOrdered = Boolean(ordered);
            const items = [];
            while (index < lines.length) {
                const current = lines[index].match(
                    isOrdered ? /^\s{0,3}\d+[.)]\s+(.+)$/ : /^\s{0,3}[-+*]\s+(.+)$/
                );
                if (!current) {
                    break;
                }
                items.push(current[1]);
                index += 1;
            }
            const List = isOrdered ? "ol" : "ul";
            blocks.push(
                <List key={`list-${blocks.length}`}>
                    {items.map((item, itemIndex) => (
                        <li key={itemIndex}>
                            <InlineMarkdown text={item} />
                        </li>
                    ))}
                </List>
            );
            continue;
        }

        const paragraphLines = [line];
        index += 1;
        while (
            index < lines.length &&
            lines[index].trim() &&
            !isBlockStart(lines[index])
        ) {
            paragraphLines.push(lines[index]);
            index += 1;
        }
        blocks.push(
            <p key={`paragraph-${blocks.length}`}>
                {paragraphLines.map((paragraphLine, lineIndex) => (
                    <React.Fragment key={lineIndex}>
                        <InlineMarkdown text={paragraphLine} />
                        {lineIndex < paragraphLines.length - 1 && <br />}
                    </React.Fragment>
                ))}
            </p>
        );
    }

    return <div className="delivery-archive-markdown">{blocks}</div>;
}

export default ArchiveMarkdown;
